<script setup lang="ts">
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/vue';
import { computed, type StyleValue } from 'vue';
interface ModalColorStep {
  idColor?: number;
  colorStep?: string;
}
interface Props {
  modelValue: boolean;
  title?: string;
  titleTag?: string;
  titleTagClass?: string;
  size?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | '7xl'
    | '8xl'
    | '9xl';
  containerClass?: string;
  panelStyle?: StyleValue;
  contentClass?: string;
  footerClass?: string;
  bgClass?: string;
  overlay?: boolean;
  screenPosition?: string;
  hiddenTitle?: boolean;
  closeToClickOutside?: boolean;
  titleClass?: string;
  titleClassContainer?: string;
  subtitle?: string;
  subtitleClass?: string;
  titleImageSrc?: string;
  titleImageAlt?: string;
  titleImageClass?: string;
  titleContentClass?: string;
  backgroundClass?: string;
  hideCloseButton?: boolean;
  currentColorStep?: ModalColorStep[];
  currentEventStep?: boolean;
  mobileFullscreen?: boolean;
  icon?: string;
  isIcon?: boolean;
  iconClass?: string;
  transitionSpeed?: 'normal' | 'slow';
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  titleTag: '',
  titleTagClass: '',
  size: 'md',
  containerClass: '',
  panelStyle: undefined,
  bgClass: 'bg-background',
  contentClass: '',
  footerClass: '',
  overlay: true,
  screenPosition: 'inset-0',
  hiddenTitle: false,
  closeToClickOutside: true,
  titleClass: '',
  titleClassContainer: '',
  subtitle: '',
  subtitleClass: 'text-sm text-white/75',
  titleImageSrc: '',
  titleImageAlt: 'title image',
  titleImageClass: 'size-16 rounded-2xl object-cover',
  titleContentClass: 'flex flex-col',
  backgroundClass: 'bg-black/75',
  hideCloseButton: false,
  currentColorStep: () => [],
  currentEventStep: false,
  mobileFullscreen: false,
  icon: 'material-symbols:wallet',
  isIcon: false,
  iconClass: 'size-8 pr-2 text-white',
  transitionSpeed: 'normal',
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'on:close': [];
}>();

const slots = defineSlots<{
  header(): void;
  default(): void;
  footer(): void;
}>();

const show = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

function closeModalToClickOutside() {
  if (!props.closeToClickOutside) return;
  show.value = false;
  emit('on:close');
}

function closeModal() {
  show.value = false;
  emit('on:close');
}
const bgClass = computed(() => `${props.bgClass}`);
const maxWidthClass = computed(() => `max-w-${props.size}`);
const containerClass = computed(
  () => `${bgClass.value} ${maxWidthClass.value} ${props.containerClass}`,
);

const dialogPanelClass = computed(() => {
  const baseClasses =
    'w-full overflow-x-hidden text-left align-middle shadow-xl transition-all';
  const mobileClasses = props.mobileFullscreen
    ? 'rounded-none h-screen max-h-screen sm:rounded-xl sm:h-auto sm:max-h-none'
    : 'rounded-xl';
  return `${baseClasses} ${mobileClasses}`;
});

const containerWrapperClass = computed(() => {
  const baseClasses = 'flex min-h-full text-center';
  const mobileClasses = props.mobileFullscreen
    ? 'p-0 items-stretch sm:p-4 sm:items-center sm:justify-center'
    : 'p-4 items-center justify-center';
  return `${baseClasses} ${mobileClasses}`;
});

const overlayEnterClass = computed(() =>
  props.transitionSpeed === 'slow'
    ? 'duration-500 ease-out'
    : 'duration-300 ease-out',
);
const overlayLeaveClass = computed(() =>
  props.transitionSpeed === 'slow'
    ? 'duration-300 ease-in'
    : 'duration-200 ease-in',
);
const panelEnterClass = computed(() =>
  props.transitionSpeed === 'slow'
    ? 'duration-500 ease-out'
    : 'duration-300 ease-out',
);
const panelLeaveClass = computed(() =>
  props.transitionSpeed === 'slow'
    ? 'duration-300 ease-in'
    : 'duration-200 ease-in',
);
</script>

<template>
  <TransitionRoot
    appear
    :show="show"
    as="template"
    @after-leave="emit('on:close')"
  >
    <Dialog
      :unmount="true"
      as="div"
      class="relative z-[900000]"
      @close="closeModalToClickOutside"
    >
      <TransitionChild
        v-if="overlay"
        as="template"
        :enter="overlayEnterClass"
        enter-from="opacity-0"
        enter-to="opacity-100"
        :leave="overlayLeaveClass"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0" :class="backgroundClass"></div>
      </TransitionChild>

      <div class="fixed overflow-y-auto" :class="screenPosition">
        <div :class="containerWrapperClass">
          <TransitionChild
            as="template"
            :enter="panelEnterClass"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            :leave="panelLeaveClass"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              :class="[dialogPanelClass, containerClass]"
              :style="panelStyle"
            >
              <div
                v-if="currentColorStep && currentColorStep.length > 0"
                class="flex justify-center gap-[8px] pt-4"
              >
                <div
                  v-for="(color, index) in currentColorStep"
                  :key="color.idColor"
                  class="flex items-center justify-center gap-1"
                >
                  <!-- Círculo con el color correspondiente -->
                  <div
                    class="flex size-[40px] items-center justify-center rounded-full text-[20.87px]"
                    :style="{ backgroundColor: color.colorStep }"
                  >
                    <p class="text-center font-bold text-white">
                      {{ color.idColor }}
                    </p>
                  </div>
                  <!-- Línea entre los círculos -->
                  <div
                    v-if="index < currentColorStep.length - 1"
                    class="h-[2px] w-[16px] rounded-full bg-checkGreen"
                    :style="{ backgroundColor: color.colorStep }"
                  ></div>
                </div>
              </div>
              <DialogTitle
                v-if="!hiddenTitle"
                as="h3"
                class="relative flex items-start gap-2 border-b-white p-4 pb-2 text-lg font-medium leading-6 text-white"
                :class="titleClassContainer"
              >
                <!-- agregar icono de una wallet -->
                <div class="flex items-center gap-3">
                  <img
                    v-if="titleImageSrc"
                    :src="titleImageSrc"
                    :alt="titleImageAlt"
                    :class="titleImageClass"
                  />
                  <IconifyIcon
                    v-else-if="isIcon"
                    :icon="icon"
                    :class="iconClass || 'size-8 pr-2 text-white'"
                  />
                  <div :class="titleContentClass">
                    <p :class="titleClass">
                      {{ title }}
                    </p>
                    <p v-if="subtitle" :class="subtitleClass">
                      {{ subtitle }}
                    </p>
                    <p
                      v-if="titleTag"
                      :class="[
                        'mt-1 inline-flex w-fit items-center rounded-full bg-primary px-2 text-[10px] font-bold uppercase text-white',
                        titleTagClass,
                      ]"
                    >
                      {{ titleTag }}
                    </p>
                  </div>
                </div>
                <button
                  v-if="hideCloseButton"
                  class="absolute right-4 cursor-pointer outline-0"
                  @click="closeModal"
                >
                  <IconifyIcon
                    class="size-6 md:size-5"
                    icon="material-symbols:close"
                  />
                </button>
              </DialogTitle>
              <slot name="header"></slot>
              <div :class="contentClass" class="mb-4 px-4">
                <slot name="default" />
              </div>

              <div v-if="!!slots.footer" :class="footerClass" class="px-4 pb-4">
                <slot name="footer" />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
